/**
 * @file: WeeklyActivityCard.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { useBiChartTheme } from '@euler/app/components/message/widgets/bi-visualization/chart/useBiChartTheme';
import { Typography } from '@euler/app/components/typography';
import { MK } from '@euler/app/flows/me/components/meKit';
import { SectionCard } from '@euler/app/flows/me/components/SectionCard';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ComboChart } from '@euler/lib/charts/ComboChart';
import { ComboSeries } from '@euler/lib/charts/ComboChart/types';
import { formatBiAxisTick } from '@euler/model/bi/format';
import { MemberDailyOrders } from '@euler/model/MemberPerformance';
import { parseISO } from 'date-fns';
import { memo, useMemo, useState } from 'react';
import { LayoutChangeEvent, View } from 'react-native';

const kWeekdays = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];
const kTickCount = 4;

/**
 * Rounds the axis top up to a 1/2/5 × 10ⁿ step, never below one per tick,
 * so the gridlines land on whole counts a person would pick.
 */
const niceAxisMax = (max: number) => {
  const rawStep = Math.max(max / kTickCount, 1);
  const magnitude = 10 ** Math.floor(Math.log10(rawStep));
  const step =
    [1, 2, 5, 10].map(m => m * magnitude).find(s => s >= rawStep) ??
    10 * magnitude;
  return step * kTickCount;
};

const kLine = { width: 2, smooth: false, showPoints: false };
const kChartHeight = 180;

export const WeeklyActivityCard = memo(
  ({ days }: { days: MemberDailyOrders[] }) => {
    const { rpx } = useResponsiveContext();
    const theme = useBiChartTheme();
    // The chart draws to a fixed width, known once laid out.
    const [width, setWidth] = useState(0);
    const onLayout = (e: LayoutChangeEvent) =>
      setWidth(e.nativeEvent.layout.width);

    const categories = useMemo(
      () => days.map(day => kWeekdays[parseISO(day.date).getDay()]),
      [days],
    );
    const series = useMemo<ComboSeries[]>(
      () => [
        {
          key: 'placed',
          name: '开单',
          type: 'bar',
          axis: 'left',
          color: MK.chartOpened,
          values: days.map(day => day.placed),
        },
        {
          key: 'finished',
          name: '完工',
          type: 'bar',
          axis: 'left',
          color: MK.chartFinished,
          values: days.map(day => day.finished),
        },
      ],
      [days],
    );
    const yAxis = useMemo(
      () => ({
        show: true,
        min: 0,
        max: niceAxisMax(
          Math.max(...days.flatMap(day => [day.placed, day.finished])),
        ),
        format: formatBiAxisTick,
      }),
      [days],
    );
    const bar = useMemo(() => ({ cornerRadius: rpx(3), gap: 0.4 }), [rpx]);

    return (
      <SectionCard
        title="近7天"
        icon="chart"
        action={
          <View style={{ flexDirection: 'row', gap: rpx(12) }}>
            <Legend color={MK.chartOpened} label="开单" />
            <Legend color={MK.chartFinished} label="完工" />
          </View>
        }
      >
        <View
          style={{ marginTop: rpx(12), height: rpx(kChartHeight) }}
          onLayout={onLayout}
        >
          {width > 0 && (
            <ComboChart
              width={width}
              height={rpx(kChartHeight)}
              categories={categories}
              series={series}
              xAxis={kXAxis}
              yAxis={yAxis}
              gridLines
              dataLabels={false}
              line={kLine}
              bar={bar}
              theme={theme}
            />
          )}
        </View>
      </SectionCard>
    );
  },
);

const kXAxis = { show: true, rotate: 0 as const };

const Legend = memo(({ color, label }: { color: string; label: string }) => {
  const { rpx } = useResponsiveContext();
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: rpx(4) }}>
      <View
        style={{
          width: rpx(8),
          height: rpx(8),
          borderRadius: rpx(4),
          backgroundColor: color,
        }}
      />
      <Typography.Text size={rpx(12)} color={MK.mutedInk}>
        {label}
      </Typography.Text>
    </View>
  );
});
